import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiArrowLeft, FiClock, FiAlertCircle, FiUpload, FiX, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { parseUTCToChicago, createChicagoDateTime, getChicagoNow } from '../../utils/datetime-comprehensive';
import apiService from '../../services/api';
import quizApiService from '../../services/quizApi';
import { sanitizeHtmlForViewer } from '../../lib/richtext/htmlSanitizer';
import RichTextEditorTipTap from '../../components/common/RichTextEditor';
import WeeklyInstructionsManager from '../../components/assignments/WeeklyInstructionsManager';
import BreakPeriodsManager from '../../components/assignments/BreakPeriodsManager';
import styles from './EditAssignment.module.css';

function EditAssignment() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    title: '',
    description: { 
      content_json: { type: 'doc', content: [] }, 
      content_html: '<p></p>', 
      content_plain: '' 
    },
    points: 10,
    dueDate: '',
    startDate: '',
    type: 'other',
    allowResubmission: false,
    allowWeeklySubmissions: false,
    maxWeeklySubmissions: ''
  });
  
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfBase64, setPdfBase64] = useState('');
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [assignmentData, setAssignmentData] = useState(null);
  const [originalStartDate, setOriginalStartDate] = useState('');
  const [showStartDateWarningModal, setShowStartDateWarningModal] = useState(false);
  const [pendingUpdateData, setPendingUpdateData] = useState(null);
  const [showCurrentDescription, setShowCurrentDescription] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    weeklyInstructions: false,
    breakPeriods: false
  });

  const fetchAssignmentDetails = useCallback(async () => {
    if (!id) {
      console.warn('⚠️ No assignment ID provided');
      return;
    }
    
    try {
      setDataLoading(true);
      setErrors({});
      console.log('🔍 Fetching assignment details for ID:', id);

      let response = await apiService.getAssignment(id);
      console.log('📋 Regular Assignment API response:', response);

      let data = null;
      let isQuizAssignment = false;

      if (response.success && response.assignment) {
        data = response.assignment;
        console.log('✅ Found regular assignment:', data);
      } else {
        console.log('🔄 Regular assignment not found, trying quiz assignment...');
        try {
          const quizResponse = await quizApiService.getAssignmentDetails(id);
          console.log('📋 Quiz Assignment API response:', quizResponse);
          
          if (quizResponse.success && quizResponse.data) {
            data = quizResponse.data;
            isQuizAssignment = true;
            console.log('✅ Found quiz assignment:', data);
          }
        } catch (quizError) {
          console.error('❌ Quiz assignment also not found:', quizError);
        }
      }

      if (data) {
        setAssignmentData({ ...data, isQuizAssignment });
        
        const dueDateField = isQuizAssignment ? data.due_at : data.due_date;
        console.log('📅 Due date parsing:', {
          isQuizAssignment,
          dueDateField,
          due_at: data.due_at,
          due_date: data.due_date
        });
        const chicagoDateTime = dueDateField ? parseUTCToChicago(dueDateField) : null;
        console.log('📅 Parsed Chicago date time:', chicagoDateTime);
        
        const startDateField = data.start_date;
        const chicagoStartDateTime = startDateField ? parseUTCToChicago(startDateField) : null;
        console.log('📅 Parsed Chicago start date time:', chicagoStartDateTime);
        
        const descriptionContent = data.description || data.quiz_description || '';
        const newFormData = {
          title: data.title || data.quiz_title || '',
          description: descriptionContent
            ? { 
                content_json: null, 
                content_html: descriptionContent, 
                content_plain: descriptionContent.replace(/<[^>]*>/g, '') 
              }
            : { 
                content_json: { type: 'doc', content: [] }, 
                content_html: '<p></p>', 
                content_plain: '' 
              },
          points: data.points ?? 10,
          dueDate: chicagoDateTime ? chicagoDateTime.toFormat('yyyy-MM-dd\'T\'HH:mm') : '',
          startDate: chicagoStartDateTime ? chicagoStartDateTime.toFormat('yyyy-MM-dd\'T\'HH:mm') : '',
          type: data.assignment_type || 'quiz',
          allowResubmission: data.allow_resubmission || false,
          allowWeeklySubmissions: data.allow_weekly_submissions || false,
          maxWeeklySubmissions: (data.max_weekly_submissions != null && typeof data.max_weekly_submissions === 'number') ? String(data.max_weekly_submissions) : ''
        };

        setOriginalStartDate(chicagoStartDateTime ? chicagoStartDateTime.toFormat('yyyy-MM-dd\'T\'HH:mm') : '');
        
        if (data.pdf_attachment) {
          setPdfBase64(data.pdf_attachment);
          const mockFile = {
            name: 'existing_attachment.pdf',
            size: 0
          };
          setPdfFile(mockFile);
        }
        
        setFormData(newFormData);
        setErrors({});
      } else {
        console.error('❌ Assignment not found in both regular and quiz tables');
        setErrors({ fetch: 'Assignment not found. Please check if the assignment exists and you have access to it.' });
      }
    } catch (error) {
      console.error('❌ Error fetching assignment details:', error);
      setErrors({ fetch: error.message || 'Failed to load assignment details' });
    } finally {
      setDataLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchAssignmentDetails();
    }
  }, [id, fetchAssignmentDetails]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleDescriptionChange = (value) => {
    setFormData(prev => ({
      ...prev,
      description: value
    }));
  };

  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      setErrors(prev => ({ ...prev, pdfFile: 'Please select a PDF file' }));
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, pdfFile: 'File size must be less than 10MB' }));
      return;
    }

    try {
      const base64 = await convertFileToBase64(file);
      setPdfFile(file);
      setPdfBase64(base64);
      setErrors(prev => ({ ...prev, pdfFile: '' }));
    } catch (error) {
      setErrors(prev => ({ ...prev, pdfFile: 'Error processing file' }));
    }
  };

  const removePdfFile = () => {
    setPdfFile(null);
    setPdfBase64('');
    setErrors(prev => ({ ...prev, pdfFile: '' }));
  };

  const setDateShortcut = (days) => {
    const now = getChicagoNow();
    const targetDate = now.plus({ days }).set({ hour: 19, minute: 0, second: 0, millisecond: 0 });
    const formattedDate = targetDate.toFormat('yyyy-MM-dd\'T\'HH:mm');
    handleInputChange('dueDate', formattedDate);
  };

  const clearDueDate = () => {
    handleInputChange('dueDate', '');
  };

  const setPointsShortcut = (points) => {
    setFormData(prev => ({
      ...prev,
      points: points
    }));
  };

  const submitAssignmentUpdate = async (updateData) => {
    let response;
    if (assignmentData?.isQuizAssignment) {
      console.log('🔄 Updating quiz assignment...');
      response = await quizApiService.updateAssignment(id, updateData);
    } else {
      console.log('🔄 Updating regular assignment...');
      response = await apiService.updateAssignment(id, updateData);
    }

    if (response.success) {
      console.log('✅ Assignment updated successfully');
      navigate('/assignments');
    } else {
      console.error('❌ Failed to update assignment:', response);
      setErrors({ submit: response.message || 'Failed to update assignment' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    try {
      const newErrors = {};
      if (!formData.title.trim()) newErrors.title = 'Title is required';
      if (formData.points === '' || formData.points === null || formData.points === undefined || Number(formData.points) < 0) {
        newErrors.points = 'Points must be 0 or greater';
      }
      
      let hasInstructions = false;
      if (formData.description) {
        if (typeof formData.description === 'object') {
          if (formData.description.content_plain !== undefined) {
            const hasPlainText = formData.description.content_plain && formData.description.content_plain.trim() !== '';
            const hasHtml = formData.description.content_html && formData.description.content_html.trim() !== '' && formData.description.content_html !== '<p></p>';
            hasInstructions = hasPlainText || hasHtml;
          } else {
            const hasText = formData.description.text && formData.description.text.trim() !== '';
            const hasHtml = formData.description.html && formData.description.html.trim() !== '' && formData.description.html !== '<p></p>';
            hasInstructions = hasText || hasHtml;
          }
        } else if (typeof formData.description === 'string') {
          hasInstructions = formData.description.trim() !== '';
        }
      }
      
      if (!hasInstructions) {
        newErrors.description = 'Instructions are required';
      }

      if (formData.allowWeeklySubmissions) {
        if (!formData.maxWeeklySubmissions || parseInt(formData.maxWeeklySubmissions) < 1) {
          newErrors.maxWeeklySubmissions = 'Maximum weekly submissions is required when weekly submissions are enabled';
        }
        if (!formData.startDate) {
          newErrors.startDate = 'Start date & time is required when weekly submissions are enabled';
        }
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        setLoading(false);
        return;
      }

      // Extract description HTML from TipTap format
      let processedDescription = '';
      if (typeof formData.description === 'object') {
        // TipTap format: { content_json, content_html, content_plain }
        processedDescription = formData.description.content_html || formData.description.html || '';
      } else if (typeof formData.description === 'string') {
        processedDescription = formData.description;
      }

      // Build update data, only including optional fields if they have values
      const updateData = {
        title: formData.title.trim(),
        description: processedDescription,
        points: parseInt(formData.points),
        type: formData.type,
        allowResubmission: formData.allowResubmission,
        allowWeeklySubmissions: formData.allowWeeklySubmissions
      };

      // Only include optional fields if they have actual values
      if (formData.allowWeeklySubmissions && formData.maxWeeklySubmissions) {
        updateData.maxWeeklySubmissions = parseInt(formData.maxWeeklySubmissions);
      }
      if (pdfBase64) {
        updateData.pdfAttachment = pdfBase64;
      }
      if (formData.startDate) {
        const chicagoStartDateTime = createChicagoDateTime(
          formData.startDate.split('T')[0],
          formData.startDate.split('T')[1]
        );
        updateData.startDate = chicagoStartDateTime.toUTC().toISO();
      }
      if (formData.dueDate) {
        const chicagoDateTime = createChicagoDateTime(
          formData.dueDate.split('T')[0],
          formData.dueDate.split('T')[1]
        );
        updateData.dueDate = chicagoDateTime.toUTC().toISO();
      }

      // Safety prompt: changing weekly assignment start date shifts weekly availability timeline
      // and can effectively reset which weeks are currently available.
      if (formData.allowWeeklySubmissions) {
        const currentStart = formData.startDate || '';
        const previousStart = originalStartDate || '';
        const startDateChanged = currentStart !== previousStart;

        if (startDateChanged) {
          setPendingUpdateData(updateData);
          setShowStartDateWarningModal(true);
          setLoading(false);
          return;
        }
      }

      console.log('📤 Updating assignment with data:', updateData);
      await submitAssignmentUpdate(updateData);
    } catch (error) {
      console.error('❌ Error updating assignment:', error);
      setErrors({ submit: error.message || 'Failed to update assignment' });
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/assignments');
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleConfirmStartDateChange = async () => {
    if (!pendingUpdateData) {
      setShowStartDateWarningModal(false);
      return;
    }

    try {
      setLoading(true);
      setErrors({});
      await submitAssignmentUpdate(pendingUpdateData);
      setOriginalStartDate(formData.startDate || '');
    } catch (error) {
      console.error('❌ Error updating assignment after confirmation:', error);
      setErrors({ submit: error.message || 'Failed to update assignment' });
    } finally {
      setShowStartDateWarningModal(false);
      setPendingUpdateData(null);
      setLoading(false);
    }
  };

  const handleCancelStartDateChange = () => {
    setShowStartDateWarningModal(false);
    setPendingUpdateData(null);
  };

  if (dataLoading) {
    return (
      <div className={styles.pageContainer}>
        <div className={styles.contentWrapper}>
          <div className={styles.headerBar}>
            <div className={styles.headerLeft}>
              <button className={styles.backButton} onClick={handleBack}>
                <FiArrowLeft size={16} />
                Back to Assignments
              </button>
              <h1 className={styles.pageTitle}>Edit Assignment</h1>
            </div>
          </div>
          <div className={styles.mainFormCard}>
            <div className={styles.loadingState}>
              <FiClock size={20} />
              Loading assignment details...
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (errors.fetch) {
    return (
      <div className={styles.pageContainer}>
        <div className={styles.contentWrapper}>
          <div className={styles.headerBar}>
            <div className={styles.headerLeft}>
              <button className={styles.backButton} onClick={handleBack}>
                <FiArrowLeft size={16} />
                Back to Assignments
              </button>
              <h1 className={styles.pageTitle}>Edit Assignment</h1>
            </div>
          </div>
          <div className={styles.mainFormCard}>
            <div className={styles.errorState}>
              <FiAlertCircle size={20} />
              {errors.fetch}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.pageContainer}>
      <div className={styles.contentWrapper}>
        {/* Header */}
        <div className={styles.headerBar}>
          <div className={styles.headerLeft}>
            <button className={styles.backButton} onClick={handleBack}>
              <FiArrowLeft size={16} />
              Back to Assignments
            </button>
            <h1 className={styles.pageTitle}>Edit Assignment</h1>
            <p className={styles.pageSubtitle}>Update assignment details and instructions.</p>
          </div>
        </div>

        {/* Course Context Card */}
        {assignmentData && (
          <div className={styles.courseContextCard}>
            <div className={styles.courseHeaderContent}>
              {/* Top Row: Course Code + Course Name */}
              <div className={styles.headerTopRow}>
                <div className={styles.courseTitleRow}>
                  <span className={styles.courseCodePill}>{assignmentData?.course_code || 'Unknown Code'}</span>
                  <h3 className={styles.courseTitle}>{assignmentData?.course_name || 'Unknown Course'}</h3>
                </div>
              </div>
              
              {/* Meta Row: Instructor, Due Date, Posted Date, Points */}
              <div className={styles.metaRow}>
                <span className={styles.metaItem}>
                  <span className={styles.metaLabel}>Instructor</span>
                  <span className={styles.metaValue}>{assignmentData?.instructor_name || '—'}</span>
                </span>
                
                <span className={styles.metaItem}>
                  <span className={styles.metaLabel}>Due</span>
                  <span className={styles.metaValue}>
                    {(() => {
                      const dueDateField = assignmentData?.due_date || assignmentData?.due_at;
                      if (!dueDateField) return 'No due date';
                      try {
                        const chicagoDate = parseUTCToChicago(dueDateField);
                        return chicagoDate ? chicagoDate.toFormat('MMM d, yyyy • h:mm a') : 'No due date';
                      } catch (e) {
                        return 'No due date';
                      }
                    })()}
                  </span>
                </span>
                
                <span className={styles.metaItem}>
                  <span className={styles.metaLabel}>Posted</span>
                  <span className={styles.metaValue}>
                    {(() => {
                      const postedDate = assignmentData?.posted_date || assignmentData?.created_at;
                      if (!postedDate) return '—';
                      try {
                        const chicagoDate = parseUTCToChicago(postedDate);
                        return chicagoDate ? chicagoDate.toFormat('MMM d, yyyy • h:mm a') : '—';
                      } catch (e) {
                        return '—';
                      }
                    })()}
                  </span>
                </span>
                
                <span className={styles.metaItem}>
                  <span className={styles.metaLabel}>Total</span>
                  <span className={styles.metaValue}>
                    {assignmentData?.points || formData.points || 0} pts
                  </span>
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Form - Single Column Layout */}
        <form className={styles.form} onSubmit={handleSubmit}>
          {/* Assignment Title */}
          <motion.div
            className={styles.mainFormCard}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className={styles.formGroup}>
              <label className={styles.label}>
                Assignment Title <span className={styles.required}>*</span>
              </label>
              <input
                type="text"
                className={styles.input}
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder="Enter assignment title"
              />
              {errors.title && <div className={styles.errorMessage}>{errors.title}</div>}
            </div>
          </motion.div>

          {/* Instructions - Rich Text Editor */}
          <motion.div
            className={styles.mainFormCard}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.15 }}
          >
            <div className={styles.formGroup}>
              <div className={styles.labelRow}>
                <label className={styles.label}>
                  Instructions <span className={styles.required}>*</span>
                </label>
                {assignmentData?.description && (
                  <div className={styles.toggleGroup}>
                    <label className={styles.toggleSwitch}>
                      <input
                        type="checkbox"
                        checked={showCurrentDescription}
                        onChange={(e) => setShowCurrentDescription(e.target.checked)}
                      />
                      <span />
                    </label>
                    <label className={styles.toggleLabel} htmlFor="showCurrentDescription" style={{ margin: 0, cursor: 'pointer' }}>
                      Preview current description
                    </label>
                  </div>
                )}
              </div>
              {showCurrentDescription && assignmentData?.description && (
                <motion.div
                  className={styles.currentDescriptionCard}
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                  style={{ marginBottom: '1rem' }}
                >
                  <h3 className={styles.currentDescriptionLabel}>Current Description</h3>
                  <div
                    className={styles.currentDescription}
                    dangerouslySetInnerHTML={{ 
                      __html: sanitizeHtmlForViewer(assignmentData.description || 'No description provided') 
                    }}
                  />
                </motion.div>
              )}
              <div className={styles.richTextEditor}>
                <RichTextEditorTipTap
                  value={formData.description}
                  onChange={handleDescriptionChange}
                  placeholder=""
                  enableImages
                />
              </div>
              {errors.description && <div className={styles.errorMessage}>{errors.description}</div>}
            </div>
          </motion.div>

          {/* Settings Card - Full Width Below Editor */}
          <motion.div
            className={styles.settingsPanelCard}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
          >
            <h3 className={styles.sectionTitle}>Settings</h3>
            
            {/* Grading and Scheduling Groups - Side by Side */}
            <div className={styles.gradingSchedulingRow}>
              {/* Grading Group */}
              <div className={styles.settingsGroup}>
                <div className={styles.groupHeader}>
                  <h4 className={styles.groupTitle}>Grading</h4>
                </div>
                <div className={styles.groupBody}>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>
                      Points <span className={styles.required}>*</span>
                    </label>
                    <input
                      type="number"
                      className={styles.input}
                      min="0"
                      value={formData.points}
                      onChange={(e) => handleInputChange('points', parseInt(e.target.value) || 0)}
                      placeholder="10"
                    />
                    {errors.points && <div className={styles.errorMessage}>{errors.points}</div>}
                    <div className={styles.chipContainer}>
                      <button type="button" className={styles.chip} onClick={() => setPointsShortcut(0)}>0</button>
                      <button type="button" className={styles.chip} onClick={() => setPointsShortcut(10)}>10</button>
                      <button type="button" className={styles.chip} onClick={() => setPointsShortcut(20)}>20</button>
                      <button type="button" className={styles.chip} onClick={() => setPointsShortcut(25)}>25</button>
                      <button type="button" className={styles.chip} onClick={() => setPointsShortcut(50)}>50</button>
                      <button type="button" className={styles.chip} onClick={() => setPointsShortcut(100)}>100</button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Scheduling Group */}
              <div className={styles.settingsGroup}>
                <div className={styles.groupHeader}>
                  <h4 className={styles.groupTitle}>Scheduling</h4>
                </div>
                <div className={styles.groupBody}>
                  {formData.allowWeeklySubmissions && (
                    <div className={styles.formGroup}>
                      <label className={styles.label}>
                        Start Date & Time <span className={styles.required}>*</span>
                      </label>
                      <input
                        type="datetime-local"
                        className={styles.input}
                        value={formData.startDate}
                        onChange={(e) => handleInputChange('startDate', e.target.value)}
                        required={formData.allowWeeklySubmissions}
                      />
                      <div className={styles.helperText}>
                        Choose the week start date. Each submission is due when the week ends.
                      </div>
                      {errors.startDate && <div className={styles.errorMessage}>{errors.startDate}</div>}
                    </div>
                  )}
                  {!formData.allowWeeklySubmissions && (
                    <div className={styles.formGroup}>
                      <label className={styles.label}>
                        Due Date & Time (Optional)
                      </label>
                      <input
                        type="datetime-local"
                        className={styles.input}
                        value={formData.dueDate}
                        onChange={(e) => handleInputChange('dueDate', e.target.value)}
                        placeholder="Leave empty for no due date"
                      />
                      <div className={styles.chipContainer}>
                        <button type="button" className={styles.chip} onClick={() => setDateShortcut(1)}>Tomorrow</button>
                        <button type="button" className={styles.chip} onClick={() => setDateShortcut(3)}>3 days</button>
                        <button type="button" className={styles.chip} onClick={() => setDateShortcut(7)}>1 week</button>
                        <button type="button" className={styles.chip} onClick={() => setDateShortcut(14)}>2 weeks</button>
                        <button type="button" className={styles.chip} onClick={clearDueDate}>No due date</button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Submissions Group */}
            <div className={styles.settingsGroup}>
              <div className={styles.groupHeader}>
                <h4 className={styles.groupTitle}>Submissions</h4>
              </div>
              <div className={styles.groupBody}>
                <div className={styles.togglesRow}>
                  <div className={`${styles.formGroup} ${styles.toggleFormGroup}`}>
                    <div className={styles.toggleGroup}>
                      <label className={styles.toggleSwitch}>
                        <input
                          type="checkbox"
                          checked={formData.allowResubmission}
                          onChange={(e) => handleInputChange('allowResubmission', e.target.checked)}
                        />
                        <span />
                      </label>
                      <div>
                        <label className={styles.toggleLabel} htmlFor="allowResubmission">
                          Allow resubmission
                        </label>
                        <div className={styles.toggleHelper}>Students can submit multiple times</div>
                      </div>
                    </div>
                  </div>

                  <div className={`${styles.formGroup} ${styles.toggleFormGroup}`}>
                    <div className={styles.toggleGroup}>
                      <label className={styles.toggleSwitch}>
                        <input
                          type="checkbox"
                          checked={formData.allowWeeklySubmissions}
                          onChange={(e) => {
                            handleInputChange('allowWeeklySubmissions', e.target.checked);
                            if (!e.target.checked) {
                              handleInputChange('maxWeeklySubmissions', '');
                            }
                          }}
                        />
                        <span />
                      </label>
                      <div>
                        <label className={styles.toggleLabel} htmlFor="allowWeeklySubmissions">
                          Allow weekly submissions
                        </label>
                        <div className={styles.toggleHelper}>Students can submit one reflection per week</div>
                      </div>
                    </div>
                    {formData.allowWeeklySubmissions && (
                      <div className={styles.maxWeeklySubmissionsInput}>
                        <label className={styles.label}>
                          Maximum Weekly Submissions <span className={styles.required}>*</span>
                        </label>
                        <input
                          type="number"
                          className={styles.input}
                          value={formData.maxWeeklySubmissions}
                          onChange={(e) => handleInputChange('maxWeeklySubmissions', e.target.value)}
                          min="1"
                          max="52"
                          placeholder="e.g. 8"
                        />
                        <div className={styles.helperText}>
                          Students can submit one reflection per week, up to the maximum number set.
                        </div>
                        {errors.maxWeeklySubmissions && <div className={styles.errorMessage}>{errors.maxWeeklySubmissions}</div>}
                      </div>
                    )}
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <input
                    type="file"
                    className={styles.fileInput}
                    accept=".pdf"
                    onChange={handleFileChange}
                    id="pdf-upload"
                  />
                  <div
                    className={`${styles.fileUploadArea} ${errors.pdfFile ? styles.hasError : ''}`}
                    onClick={() => document.getElementById('pdf-upload').click()}
                  >
                    <div className={styles.fileUploadContent}>
                      <div className={styles.fileUploadIcon}>
                        <FiUpload />
                      </div>
                      <div className={styles.fileUploadText}>
                        {pdfFile ? 'Change PDF File' : 'Upload PDF Attachment (Optional) Max 10MB'}
                      </div>
                      <div className={styles.fileUploadSubtext}>
                        {pdfFile ? pdfFile.name : 'Click to upload or drag and drop'}
                      </div>
                    </div>
                  </div>
                  {pdfFile && (
                    <div className={styles.fileInfo}>
                      <div className={styles.fileName}>{pdfFile.name}</div>
                      {pdfFile.size > 0 && (
                        <div className={styles.fileSize}>{(pdfFile.size / 1024 / 1024).toFixed(2)} MB</div>
                      )}
                      <button type="button" className={styles.removeFileButton} onClick={removePdfFile}>
                        <FiX size={18} />
                      </button>
                    </div>
                  )}
                  {errors.pdfFile && <div className={styles.errorMessage}>{errors.pdfFile}</div>}
                </div>
              </div>
            </div>

            {/* Weekly Instructions Section */}
            {formData.allowWeeklySubmissions && (
              <div className={styles.weeklySettingsBlock}>
                <div className={styles.settingsGroup}>
                  <div className={styles.sectionHeaderRow}>
                    <h3 className={styles.sectionTitle} style={{ marginBottom: 0 }}>
                      Weekly Instructions
                    </h3>
                    <button
                      type="button"
                      className={styles.sectionToggleButton}
                      onClick={() => toggleSection('weeklyInstructions')}
                    >
                      <span>{expandedSections.weeklyInstructions ? 'Hide' : 'Show'}</span>
                      {expandedSections.weeklyInstructions ? <FiChevronUp /> : <FiChevronDown />}
                    </button>
                  </div>
                  {expandedSections.weeklyInstructions && assignmentData && (
                    <WeeklyInstructionsManager
                      assignmentId={id}
                      maxWeeks={formData.maxWeeklySubmissions ? parseInt(formData.maxWeeklySubmissions) : 52}
                      defaultInstructions={assignmentData.description || ''}
                    />
                  )}
                </div>
              </div>
            )}

            {/* Break Periods Section */}
            {formData.allowWeeklySubmissions && (
              <div className={styles.weeklySettingsBlock}>
                <div className={styles.settingsGroup}>
                  <div className={styles.sectionHeaderRow}>
                    <h3 className={styles.sectionTitle} style={{ marginBottom: 0 }}>
                      Excluded Weeks
                    </h3>
                    <button
                      type="button"
                      className={styles.sectionToggleButton}
                      onClick={() => toggleSection('breakPeriods')}
                    >
                      <span>{expandedSections.breakPeriods ? 'Hide' : 'Show'}</span>
                      {expandedSections.breakPeriods ? <FiChevronUp /> : <FiChevronDown />}
                    </button>
                  </div>
                  {expandedSections.breakPeriods && assignmentData && (
                    <BreakPeriodsManager
                      assignmentId={id}
                      isWeekly={true}
                    />
                  )}
                </div>
              </div>
            )}

            {errors.submit && (
              <div className={styles.errorMessage} style={{ marginTop: '1rem' }}>{errors.submit}</div>
            )}
          </motion.div>

          {/* Footer Actions */}
          <div className={styles.footerActions}>
            <button
              type="button"
              className={`${styles.button} ${styles.secondary}`}
              onClick={handleBack}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`${styles.button} ${styles.primary}`}
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? 'Updating...' : 'Update Assignment'}
            </button>
          </div>
        </form>

        {showStartDateWarningModal && (
          <div className={styles.confirmModalOverlay} role="dialog" aria-modal="true" aria-labelledby="start-date-warning-title">
            <div className={styles.confirmModalCard}>
              <div className={styles.confirmModalHeader}>
                <h3 id="start-date-warning-title" className={styles.confirmModalTitle}>Change Weekly Start Date?</h3>
              </div>
              <p className={styles.confirmModalMessage}>
                Changing the weekly assignment start date will reset the weekly timeline and can change which weeks are available to students.
              </p>
              <div className={styles.confirmModalActions}>
                <button
                  type="button"
                  className={`${styles.button} ${styles.secondary}`}
                  onClick={handleCancelStartDateChange}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={`${styles.button} ${styles.primary}`}
                  onClick={handleConfirmStartDateChange}
                  disabled={loading}
                >
                  Proceed
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default EditAssignment;
